import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  useTheme,
  alpha,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import type { Group } from "../types";
import { GroupCard } from "./GroupCard";
import { EmptySection } from "./EmptySection";

const INITIAL_VISIBLE = 4;

interface GroupSectionProps {
  title: string;
  groups: Group[] | undefined;
  icon: React.ReactNode;
  accentColor: string;
  emptyMessage?: string;
  onViewDetails: (groupId: string) => void;
  onJoinCircle: (circleId: string) => void;
  onDeclineCircle: (circleId: string) => void;
}

export const GroupSection: React.FC<GroupSectionProps> = ({
  title,
  groups,
  icon,
  accentColor,
  emptyMessage,
  onViewDetails,
  onJoinCircle,
  onDeclineCircle,
}) => {
  const theme = useTheme();
  const [showAll, setShowAll] = useState(false);

  const total = groups?.length ?? 0;
  const hasMore = total > INITIAL_VISIBLE;
  const displayed = showAll ? groups : groups?.slice(0, INITIAL_VISIBLE);
  const hiddenCount = total - INITIAL_VISIBLE;

  return (
    <Box>
      {/* Section header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 2.5,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            bgcolor: alpha(accentColor, 0.1),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            color: accentColor,
          }}
        >
          {icon}
        </Box>

        <Typography
          sx={{
            fontSize: "1.05rem",
            fontWeight: 700,
            color: "text.primary",
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </Typography>

        <Box
          sx={{
            minWidth: 22,
            height: 22,
            borderRadius: "11px",
            bgcolor: alpha(accentColor, 0.12),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 0.75,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 700,
              color: accentColor,
              lineHeight: 1,
            }}
          >
            {total}
          </Typography>
        </Box>
      </Box>

      {/* Cards grid */}
      {total === 0 ? (
        <EmptySection message={emptyMessage} accentColor={accentColor} />
      ) : (
        <>
          <Grid container spacing={2.5}>
            {displayed?.map((group) => (
              <GroupCard
                key={group.id}
                group={group}
                onViewDetails={onViewDetails}
                onJoinCircle={onJoinCircle}
                onDeclineCircle={onDeclineCircle}
              />
            ))}
          </Grid>

          {hasMore && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowAll((prev) => !prev)}
                endIcon={
                  showAll ? (
                    <KeyboardArrowUpIcon sx={{ fontSize: "1rem" }} />
                  ) : (
                    <KeyboardArrowDownIcon sx={{ fontSize: "1rem" }} />
                  )
                }
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  borderColor: alpha(accentColor, 0.3),
                  color: accentColor,
                  borderRadius: "8px",
                  textTransform: "none",
                  "&:hover": {
                    borderColor: accentColor,
                    bgcolor: alpha(accentColor, 0.04),
                  },
                }}
              >
                {showAll
                  ? "Show less"
                  : `Show ${hiddenCount} more`}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
